import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import moment from 'moment'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import VideoModal from '../ui/VideoModal'
import { useTableStyles } from './tableTheme'
import './challenges.css'

/*
 * THE SUBMISSIONS ON ONE CHALLENGE.
 *
 * GET  admin/users/userschallenges/:id                        the list
 * POST admin/dashboard/publishusrchallenge { ChallengeID, UserID, IsPublished }
 * Both untouched — same URLs, same payload, same headers, same reading of the
 * answer (err==0 -> reload, otherwise show what the server said).
 *
 * The old screen hid every action behind a Bootstrap modal that had to be
 * opened one player at a time. Publish and unpublish are now on the row itself
 * with an in-page confirmation, the clip opens in the shared VideoModal (which
 * walks the whole set), and the status is a pill instead of a bare 1/0.
 */

const IconPlay = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.2v13.6L19 12 8 5.2z" /></svg>
)

const IconUsers = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 12.5a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-8 8a8 8 0 0 1 16 0v.5H4v-.5z"/>
  </svg>
)

const nameOf=(row)=>{
  const joined=[row && row.FirstName,row && row.LastName].filter(Boolean).join(' ').trim()
  return joined || 'Unknown user'
}

export default function ChallengeUsers(props) {
    const [isLoading,setIsLoading]=useState(true)
    const [failed,setFailed]=useState(false)
    const [data,setData]=useState([])
    const [note,setNote]=useState(null)
    /* { row, IsPublished } — the action waiting for a yes. */
    const [pending,setPending]=useState(null)
    const [clipIndex,setClipIndex]=useState(-1)

    const tableStyles=useTableStyles()

    const rows=useMemo(()=>(Array.isArray(data) ? data : []),[data])
    const clips=useMemo(()=>rows.filter(row=>!!row.FileID),[rows])
    const open=clipIndex>=0 && clipIndex<clips.length

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: true,
            width:'66px'
        },

        {
            name: 'User',
            selector: row => nameOf(row),
            cell: row => (
                <button
                  type="button"
                  className="chx-cell-link"
                  onClick={()=>{playClip(row)}}
                  disabled={!row.FileID}
                  title={row.FileID ? 'Play video' : 'No video'}
                >
                  {nameOf(row)}
                </button>
            ),
            sortable: true,
            wrap: true,
            grow: 2,
            minWidth:'180px'
        },

        {
            name: 'Challenge',
            selector: row =>  row.Title,
            cell: row => <span className="chx-cell-wrap">{row.Title}</span>,
            sortable: true,
            wrap: true,
            grow: 2,
            minWidth:'180px'
        },

        {
          name: 'Published',
          selector: row =>  moment(row.DateCreated).format('DD/MM/YYYY'),
          cell: row => <span className="mono">{moment(row.DateCreated).format('DD/MM/YYYY')}</span>,
          sortable: true,
          width:'150px'
      },

      {
        name: 'Status',
        selector: row =>  row.IsPublished,
        cell: row => (
            row.IsPublished==1
              ? <span className="chx-pill chx-pill-good"><i aria-hidden="true"></i>Published</span>
              : <span className="chx-pill chx-pill-warn"><i aria-hidden="true"></i>Pending</span>
        ),
        sortable: true,
        width:'160px'
    },

    {
        name: '',
        cell: row => (
            <span className="chx-cell-act">
              <Btn
                onClick={()=>{playClip(row)}}
                disabled={!row.FileID}
                aria-label={'Play video — '+nameOf(row)}
                title={row.FileID ? 'Play video' : 'No video'}
              >
                <IconPlay/>
              </Btn>

              {row.IsPublished==1
                ? <Btn className="chx-btn-crit" onClick={()=>setPending({row,IsPublished:0})}>UNPUBLISH</Btn>
                : <Btn variant="accent" onClick={()=>setPending({row,IsPublished:1})}>PUBLISH</Btn>}
            </span>
        ),
        width:'230px'
    },

    ]


    useEffect(()=>{
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    const loadData=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/users/userschallenges/'+props.id,{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setData(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            console.log(err)
        })
      }

    const playClip=(row)=>{
        const index=clips.indexOf(row)
        if (index>-1) setClipIndex(index)
    }

    const closeClip=useCallback(()=>setClipIndex(-1),[])

    useEffect(()=>{
      if (clipIndex>=clips.length) setClipIndex(-1)
    },[clips.length,clipIndex])


    const activate=(item,IsPublished)=>{
         setPending(null)
         setNote(null)

         const obj={
           ChallengeID:item.ChallengeID,
           UserID:item.UserID,
           IsPublished:IsPublished
         }
        // console.log(obj)
           axios.post(process.env.REACT_APP_API_SERVER+'admin/dashboard/publishusrchallenge',obj,{
               headers: {
                   'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                   }
               })
             .then(res=>{
               if (res.data.err==0){
                   loadData()
               }else{
                 setNote({tone:'crit',text:res.data.msg})
               }


           }).catch(err=>{
             setNote({tone:'crit',text:'Something went wrong, please try again'})
           })
     }


  return (
    <Panel
      title="User Posts"
      subtitle={isLoading ? 'Loading…' : rows.length+' user submissions'}
    >

        {note && (
          <div className="chx-note chx-note-crit" role="alert">
            <span className="chx-note-text">{note.text}</span>
          </div>
        )}

        {pending && (
          <div className="chx-confirm" role="alertdialog" aria-label="Confirmation">
            <span className="chx-confirm-text">
              {pending.IsPublished==1
                ? 'PUBLISH CHALLENGE!!!! ARE YOU SURE??? — '+nameOf(pending.row)
                : 'UNPUBLISH. Are you sure? — '+nameOf(pending.row)}
            </span>
            <span className="chx-confirm-act">
              <Btn onClick={()=>setPending(null)}>Cancel</Btn>
              {pending.IsPublished==1
                ? <Btn variant="accent" onClick={()=>activate(pending.row,1)}>Yes, publish</Btn>
                : <Btn className="chx-btn-crit" onClick={()=>activate(pending.row,0)}>Yes, unpublish</Btn>}
            </span>
          </div>
        )}

        {isLoading && (
          <div className="chx-sk">
            <div className="skeleton chx-sk-line"></div>
            <div className="skeleton chx-sk-line chx-sk-short"></div>
          </div>
        )}

        {!isLoading && failed && (
          <div className="chx-note chx-note-crit" role="alert">
            <span className="chx-note-text">The submissions could not be loaded.</span>
            <Btn onClick={loadData}>Try again</Btn>
          </div>
        )}

        {!isLoading && !failed && rows.length===0 && (
          <div className="chx-empty">
            <span className="chx-empty-mark" aria-hidden="true"><IconUsers/></span>
            <h2 className="chx-empty-title">No entries</h2>
            <p className="chx-empty-text">
              No user has uploaded a video for this challenge yet.
              As soon as the first one arrives it appears here for review and publishing.
            </p>
          </div>
        )}

        {!isLoading && !failed && rows.length>0 && (
          <div className="chx-table">
            <div className="chx-table-inner chx-table-inner-wide">
              <DataTable
                columns={columns}
                data={rows}
                highlightOnHover
                customStyles={tableStyles}
                //expandableRows
                //expandableRowsComponent={ExpandedComponent}
                //pagination
                />
            </div>
          </div>
        )}

        {open && (
          <VideoModal
            items={clips.map(row=>({
              fileId: row.FileID,
              name: nameOf(row),
              subtitle: row.Title
            }))}
            index={clipIndex}
            title="User Posts"
            onIndex={setClipIndex}
            onClose={closeClip}
          />
        )}

    </Panel>
  )
}
